import React, { Component } from 'react'
import THEME_FONT_FAMILIES from '../../../constants/themeFontFamilies'

const CANVAS_MAX_WIDTH = 600

// Maps 9-cell position keys to absolute CSS coords
const POSITION_STYLE = {
    'top-left':      { top: '5%',  left: '5%' },
    'top-center':    { top: '5%',  left: '50%', transform: 'translateX(-50%)' },
    'top-right':     { top: '5%',  right: '5%' },
    'center-left':   { top: '50%', left: '5%',  transform: 'translateY(-50%)' },
    'center':        { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    'center-right':  { top: '50%', right: '5%', transform: 'translateY(-50%)' },
    'bottom-left':   { bottom: '5%', left: '5%' },
    'bottom-center': { bottom: '5%', left: '50%', transform: 'translateX(-50%)' },
    'bottom-right':  { bottom: '5%', right: '5%' },
}

function posStyle(pos) {
    return Object.assign({ position: 'absolute' }, POSITION_STYLE[pos] || POSITION_STYLE['bottom-center'])
}

function fontStack(font, systemFont) {
    if (font && font !== 'inherit') return `${font}, sans-serif`
    return systemFont ? `${systemFont}, sans-serif` : 'inherit'
}

class PresetPreview extends Component {
    render() {
        const { settings, format, themeData } = this.props
        const s    = settings  || {}
        const chosenFont       = (themeData && themeData.chosen_font) || 'affogato'
        const systemFontFamily = THEME_FONT_FAMILIES[chosenFont] || THEME_FONT_FAMILIES.affogato
        const gen  = s.general  || {}
        const bg   = s.background || {}
        const brd  = s.border   || {}
        const grad = s.gradient || {}
        const ttl  = s.title    || {}
        const ft   = s.freetext || {}
        const dt   = s.datetime || {}
        const badge = s.badge   || {}
        const fsk  = s.fsk      || {}
        const logo = s.logo     || {}
        const cta  = s.cta      || {}

        const fw = format ? format.width  : 1080
        const fh = format ? format.height : 1350
        const aspectRatio = `${fw}/${fh}`
        const canvasAspect = fw / fh

        const posterMaxWidth = gen.posterMaxWidth || 50
        const effectivePosterWidth = Math.min(posterMaxWidth, Math.floor(50 / canvasAspect))
        const posterX = gen.posterX != null ? gen.posterX : 50
        const posterY = gen.posterY != null ? gen.posterY : 50

        // Scale pixel values from design space (fw wide) to preview canvas (~600px)
        const sc = (px) => Math.max(7, Math.round((px || 0) * CANVAS_MAX_WIDTH / fw))

        // ── Border ──────────────────────────────────────────────────────────
        const borderStyle = brd.width > 0
            ? { border: `${brd.width}px ${brd.style || 'solid'} ${brd.color || '#fff'}`, boxSizing: 'border-box' }
            : {}

        let cornerStyle = {}
        if (brd.corners === 'rund') {
            cornerStyle = { borderRadius: `${brd.borderRadius || 0}px` }
        } else if (brd.corners === 'abgeschrägt') {
            const sz  = sc(brd.bevelSize || 20)
            const bc  = brd.bevelCorners || {}
            const tl  = bc.ol ? sz : 0
            const tr  = bc.or ? sz : 0
            const bl  = bc.ul ? sz : 0
            const br_ = bc.ur ? sz : 0
            cornerStyle = {
                clipPath: `polygon(
                    ${tl}px 0, calc(100% - ${tr}px) 0,
                    100% ${tr}px, 100% calc(100% - ${br_}px),
                    calc(100% - ${br_}px) 100%, ${bl}px 100%,
                    0 calc(100% - ${bl}px), 0 ${tl}px
                )`,
            }
        }

        // ── Gradient overlay ────────────────────────────────────────────────
        let gradientOverlay = null
        if (grad.enabled && grad.stops && grad.stops.length >= 2) {
            const sortedStops = grad.stops
                .slice()
                .sort((a, b) => a.position - b.position)
                .map(stop => `${stop.color} ${stop.position}%`)
                .join(', ')
            let gradCss
            if (grad.type === 'radial') {
                gradCss = `radial-gradient(circle, ${sortedStops})`
            } else if (grad.type === 'conic') {
                gradCss = `conic-gradient(${sortedStops})`
            } else {
                gradCss = `linear-gradient(${grad.angle || 180}deg, ${sortedStops})`
            }
            gradientOverlay = (
                <div style={{ position: 'absolute', inset: 0, background: gradCss, pointerEvents: 'none' }} />
            )
        }

        // ── Text group: title → freetext → datetime stacked ────────────────
        // Anchor at title's position (9-cell, same as badge/fsk/logo); box
        // shrinks to its content so left/right anchors actually move it.
        const groupAnchor = ttl.position || 'bottom-center'
        const textRowStyle = (data, defaults) => {
            const style = {
                color:      data.color || defaults.color,
                fontSize:   `${sc(data.size || defaults.size)}px`,
                fontFamily: fontStack(data.font, systemFontFamily),
                textAlign:  data.align || defaults.align || 'center',
                lineHeight: 1.3,
            }
            if (data.backgroundColor && data.backgroundColor !== 'transparent') {
                style.background = data.backgroundColor
                style.padding = '1px 4px'
            }
            return style
        }

        // ── Badge shape ──────────────────────────────────────────────────────
        const badgeShapeStyle = badge.shape === 'circle'   ? { borderRadius: '50%' }
            : badge.shape === 'rounded' ? { borderRadius: `${badge.borderRadius || 0}px` }
            : {}

        return (
            <div className="preset-preview">
                <div className="preset-preview__canvas" style={{
                    background:  bg.color || '#1a1a2e',
                    position:    'relative',
                    overflow:    'hidden',
                    width:       '100%',
                    aspectRatio: aspectRatio,
                    maxWidth:    `${CANVAS_MAX_WIDTH}px`,
                    margin:      '0 auto',
                    ...borderStyle,
                    ...cornerStyle,
                }}>

                    {/* Background image */}
                    {bg.imageUrl && (
                        <img src={bg.imageUrl} alt="" style={{
                            position:   'absolute',
                            inset:      0,
                            width:      '100%',
                            height:     '100%',
                            objectFit:  'cover',
                            pointerEvents: 'none',
                        }} />
                    )}

                    {/* Gradient overlay */}
                    {gradientOverlay}

                    {/* Poster placeholder */}
                    <div style={{
                        position:  'absolute',
                        left:      `${posterX}%`,
                        top:       `${posterY}%`,
                        transform: 'translate(-50%, -50%)',
                        width:     `${effectivePosterWidth}%`,
                    }}>
                        <div style={{
                            background:  'linear-gradient(180deg, #28183A 0%, #0E121C 100%)',
                            aspectRatio: '2/3',
                        }} />
                    </div>

                    {/* Text group: title → freetext → datetime, stacked at title's anchor */}
                    <div style={Object.assign({ width: 'fit-content', maxWidth: '90%', pointerEvents: 'none' }, posStyle(groupAnchor))}>
                        <div style={textRowStyle(ttl, { align: 'center', size: 72, color: '#ffffff' })}>
                            {ttl.defaultText || 'Filmtitel'}
                        </div>
                        <div style={textRowStyle(ft, { align: 'center', size: 48, color: '#ffffff' })}>
                            {ft.defaultText || 'Freitext'}
                        </div>
                        <div style={textRowStyle(dt, { align: 'center', size: 36, color: '#C8C0B0' })}>
                            {'04.02. 20:15 Uhr'}
                        </div>
                    </div>

                    {/* Badge / Störer */}
                    {badge.enabled !== false && (
                        <div style={{
                            position:        'absolute',
                            left:            `${badge.x != null ? badge.x : 80}%`,
                            top:             `${badge.y != null ? badge.y : 60}%`,
                            transform:       'translate(-50%, -50%)',
                            width:           `${sc(badge.width || 200)}px`,
                            height:          `${sc(badge.height || 200)}px`,
                            background:      badge.backgroundColor || '#ef7d00',
                            color:           badge.color || '#fff',
                            display:         'flex',
                            alignItems:      'center',
                            justifyContent:  'center',
                            fontFamily:      fontStack(badge.font, systemFontFamily),
                            fontSize:        `${Math.max(6, sc((badge.width || 200) / 4))}px`,
                            fontWeight:      700,
                            textAlign:       'center',
                            overflow:        'hidden',
                            pointerEvents:   'none',
                            ...badgeShapeStyle,
                        }}>
                            {badge.defaultText || 'NEU'}
                        </div>
                    )}

                    {/* FSK badge */}
                    {fsk.enabled && (
                        <div style={{
                            ...posStyle(fsk.position || 'top-right'),
                            width:          `${sc(fsk.size || 100)}px`,
                            height:         `${sc(fsk.size || 100)}px`,
                            background:     '#cc0000',
                            borderRadius:   '50%',
                            display:        'flex',
                            alignItems:     'center',
                            justifyContent: 'center',
                            fontFamily:     fontStack(fsk.font, systemFontFamily),
                            fontSize:       `${Math.max(6, sc((fsk.size || 100) / 2.5))}px`,
                            color:          '#fff',
                            fontWeight:     900,
                            pointerEvents:  'none',
                        }}>
                            FSK
                        </div>
                    )}

                    {/* Logo */}
                    {logo.imageUrl && (
                        <img
                            src={logo.imageUrl}
                            alt=""
                            style={{
                                ...posStyle(logo.position || 'top-left'),
                                width:     `${sc(logo.size || 150)}px`,
                                height:    'auto',
                                maxWidth:  '30%',
                                pointerEvents: 'none',
                            }}
                        />
                    )}

                    {/* CTA */}
                    <div style={Object.assign({
                        color:      cta.color || '#ffffff',
                        background: cta.backgroundColor && cta.backgroundColor !== 'transparent' ? cta.backgroundColor : '#ef7d00',
                        fontFamily: fontStack(cta.font, systemFontFamily),
                        fontSize:   `${sc(cta.size || 32)}px`,
                        fontWeight: 700,
                        textAlign:  cta.align || 'center',
                        padding:    `${sc(8)}px ${sc(20)}px`,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                    }, posStyle(cta.position || 'bottom-center'))}>
                        {'Jetzt Tickets sichern'}
                    </div>

                </div>
            </div>
        )
    }
}

export default PresetPreview
